import React, { ChangeEvent, useCallback, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { Button, CircularProgress, Menu, MenuItem } from "@mui/material";
import { useAccountSelector, useDebtActions, useReportsActions } from "store";
import { RefetchReport, ReportsFileDataType, RoutesKeys } from "store/types";

import { Routes } from "../../../constants/routes";

// interface Option {
//   title: string;
//   dataType: ReportsFileDataType;
// }

interface UploadParams {
  file?: File;
  dataType: ReportsFileDataType;
}

type RefetchReportCollection = Partial<Record<RoutesKeys, RefetchReport>>;

const refetchReportCollection: RefetchReportCollection = {
  dealReport: "deal",
  snapshot: "portfolio",
  timeline: "timeline",
  comparison: "comparison"
};

export const DataUploader: React.FC<{ isHamburgerMenu?: boolean }> = ({ isHamburgerMenu = false }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // const inputRef = useRef<HTMLInputElement>(null);
  const inputAllRef = useRef<HTMLInputElement>(null);
  const { investor_level_characteristics, show_debt } = useAccountSelector() || {};

  const { uploadFileAction, getMetaAction, refetchReportAction } = useReportsActions();
  const { importDebtAction } = useDebtActions();
  const { pathname } = useLocation();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const upload = useCallback(async ({ dataType, file }: UploadParams) => {
    try {
      if (!file) return;
      setIsLoading(true);
      setAnchorEl(null);
      await uploadFileAction(file, dataType);
      getMetaAction();

      const reportRoute: keyof RefetchReportCollection | undefined = (
        Object.keys(refetchReportCollection) as RoutesKeys[]
      ).find((routeKey) => matchPath(pathname, Routes[routeKey]));

      if (!reportRoute) return;
      const report = refetchReportCollection[reportRoute];

      report && refetchReportAction(report);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleUploadAll = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      for (let i = 0; i < e.target.files.length; i++) {
        const file = e.target.files.item(i) as File;
        if (file.name.toLowerCase() === "data.csv") {
          upload({ file, dataType: "data" });
        } else if (file.name.toLowerCase() === "characteristics.csv") {
          upload({ file, dataType: "characteristic" });
        } else if (file.name.toLowerCase() === "measures.csv") {
          upload({ file, dataType: "measure" });
        } else if (file.name.toLowerCase() === "metadata.csv") {
          upload({ file, dataType: "metadata" });
        } else if (file.name.toLowerCase() === "measuresassetclasses.csv") {
          upload({ file, dataType: "measure_asset_class" });
        } else if (file.name.toLowerCase() === "investor_characteristics.csv" && investor_level_characteristics) {
          upload({ file, dataType: "investor_characteristic" });
        } else if (
          file.name.toLowerCase() === "investor_rounds_characteristics.csv" &&
          investor_level_characteristics
        ) {
          upload({ file, dataType: "investor_rounds_characteristic" });
        }
      }
    }
  };

  const handleUploadCsv = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      upload({ file: e.target.files[0], dataType: "data" });
    }
  };
  const handleUploadMetadata = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      upload({ file: e.target.files[0], dataType: "metadata" });
    }
  };
  const handleUploadMeasures = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      upload({ file: e.target.files[0], dataType: "measure" });
    }
  };
  const handleUploadCharacteristics = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      upload({ file: e.target.files[0], dataType: "characteristic" });
    }
  };
  const handleUploadInvestors = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      upload({ file: e.target.files[0], dataType: "investor" });
    }
  };
  const handleUploadMeasureAssetClasses = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      upload({ file: e.target.files[0], dataType: "measure_asset_class" });
    }
  };

  const handleUploadInvestorCharacteristics = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length && investor_level_characteristics) {
      upload({ file: e.target.files[0], dataType: "investor_characteristic" });
    }
  };
  const handleUploadInvestorCharacteristicsByRound = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length && investor_level_characteristics) {
      upload({ file: e.target.files[0], dataType: "investor_rounds_characteristic" });
    }
  };

  const handleUploadDebt = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    try {
      setIsLoading(true);
      setAnchorEl(null);
      await importDebtAction(Array.from(e.target.files));
    } finally {
      setIsLoading(false);
    }
  };

  const menuItemStyles = {
    ":hover": {
      backgroundColor: "#dee7fa",
      transition: "background-color 0.2s ease"
    }
  };

  return (
    <>
      <Button
        onClick={handleClick}
        sx={{
          padding: "5px 10px",
          ":hover": { background: "#0f3a54", color: "#ffffff" },
          color: "#92a7bc",
          cursor: "pointer",
          borderRadius: "8px",
          fontSize: isHamburgerMenu ? "20px" : "15px",
          display: "flex",
          lineHeight: "1.5",
          justifyContent: "flex-start"
        }}
      >
        {isLoading ? <CircularProgress size={25} /> : "Upload"}
      </Button>
      <input type="file" multiple ref={inputAllRef} hidden={true} onChange={handleUploadAll} />
      <Menu id="basic-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={() => {}} sx={{ ...menuItemStyles }}>
          <label style={{ marginBottom: 0, width: "100%", cursor: "pointer" }}>
            Upload Data
            <input type="file" hidden={true} onChange={handleUploadCsv} />
          </label>
        </MenuItem>
        <MenuItem onClick={() => {}} sx={{ ...menuItemStyles }}>
          <label style={{ marginBottom: 0, width: "100%", cursor: "pointer" }}>
            Upload Metadata
            <input type="file" hidden={true} onChange={handleUploadMetadata} />
          </label>
        </MenuItem>
        <MenuItem onClick={() => {}} sx={{ ...menuItemStyles }}>
          <label style={{ marginBottom: 0, width: "100%", cursor: "pointer" }}>
            Upload Measures
            <input type="file" hidden={true} onChange={handleUploadMeasures} />
          </label>
        </MenuItem>
        <MenuItem onClick={() => {}} sx={{ ...menuItemStyles }}>
          <label style={{ marginBottom: 0, width: "100%", cursor: "pointer" }}>
            Upload Measures-Asset Classes
            <input type="file" hidden={true} onChange={handleUploadMeasureAssetClasses} />
          </label>
        </MenuItem>
        <MenuItem onClick={() => {}} sx={{ ...menuItemStyles }}>
          <label style={{ marginBottom: 0, width: "100%", cursor: "pointer" }}>
            Upload Characteristics
            <input type="file" hidden={true} onChange={handleUploadCharacteristics} />
          </label>
        </MenuItem>
        <MenuItem onClick={() => {}} sx={{ ...menuItemStyles }}>
          <label style={{ marginBottom: 0, width: "100%", cursor: "pointer" }}>
            Upload Investors
            <input type="file" hidden={true} onChange={handleUploadInvestors} />
          </label>
        </MenuItem>
        <MenuItem onClick={() => {}} sx={{ ...menuItemStyles }}>
          <label style={{ marginBottom: 0, width: "100%", cursor: "pointer" }}>
            Upload All
            <input type="file" multiple hidden={true} onChange={handleUploadAll} />
          </label>
        </MenuItem>
        {show_debt && (
          <MenuItem onClick={() => {}} sx={{ ...menuItemStyles }}>
            <label style={{ marginBottom: 0, width: "100%", cursor: "pointer" }}>
              Upload Debt
              <input type="file" accept=".csv" multiple hidden={true} onChange={handleUploadDebt} />
            </label>
          </MenuItem>
        )}
        {investor_level_characteristics && (
          <MenuItem onClick={() => {}} sx={{ ...menuItemStyles }}>
            <label style={{ marginBottom: 0, width: "100%", cursor: "pointer" }}>
              Upload Investor Characteristics
              <input type="file" multiple hidden={true} onChange={handleUploadInvestorCharacteristics} />
            </label>
          </MenuItem>
        )}
        {investor_level_characteristics && (
          <MenuItem onClick={() => {}} sx={{ ...menuItemStyles }}>
            <label style={{ marginBottom: 0, width: "100%", cursor: "pointer" }}>
              Upload Investor Characteristics by Round
              <input type="file" multiple hidden={true} onChange={handleUploadInvestorCharacteristicsByRound} />
            </label>
          </MenuItem>
        )}
        {/* {options.map(({ title, dataType }) => { */}
        {/* return ( */}
        {/* <MenuItem key={title + dataType} onClick={() => inputRef.current?.click()}>
              <input
                key={title + dataType}
                type="file"
                ref={inputRef}
                hidden={true}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  upload({ file: e.target.files?.[0], dataType });
                }}
              />
              {title}
            </MenuItem> */}
        {/* ); */}
        {/* })} */}
      </Menu>
    </>
  );
};
